import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ProductService from "../../rest/productService";
import CustomButton from "../lib/customButton";
import SearchInput from "./searchInput";
import { STOCK_ADJUSTMENT_ACTIONS, STOCK_ADJUSTMENT_REASONS, OTHER_REASON_CODE } from "../../constants/stockAdjustmentReasons";

// Modal do botão "Atualizar Estoque" (sidebar/admin), replicando a mesma
// estrutura/UX do expenseModal.jsx: dropdown de motivo fechado + "Outro" com
// texto livre obrigatório, CustomButton para ações, toast de sucesso/erro.
// Expõe as 2 ações validadas por Matheus em PUT /estoque/ajusteManual:
// RETURN (adicionar quantidade) e REMOVE (remover quantidade).
const StockAdjustmentModal = ({ isOpen, onClose }) => {
    const [search, setSearch] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const [action, setAction] = useState("");
    const [quantity, setQuantity] = useState("");
    const [reasonCode, setReasonCode] = useState("");
    const [reasonText, setReasonText] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setSearch("");
            setSearchResults([]);
            setSearching(false);
            setSelectedProduct(null);
            setAction("");
            setQuantity("");
            setReasonCode("");
            setReasonText("");
            setLoading(false);
        }
    }, [isOpen]);

    // Reaproveita o mesmo mecanismo de busca de produto já usado em
    // pages/storage (ProductService.getAllProducts com "search"), com o
    // mesmo debounce de 300ms do useProductData.js.
    useEffect(() => {
        if (!isOpen || selectedProduct || !search.trim()) {
            setSearchResults([]);
            return;
        }

        let isCurrent = true;
        setSearching(true);

        const timer = setTimeout(async () => {
            try {
                const response = await ProductService.getAllProducts({ search, page: 0, size: 8 });
                if (isCurrent) setSearchResults(response?.content || []);
            } catch (error) {
                console.error(error);
                if (isCurrent) setSearchResults([]);
            } finally {
                if (isCurrent) setSearching(false);
            }
        }, 300);

        return () => {
            isCurrent = false;
            clearTimeout(timer);
        };
    }, [search, isOpen, selectedProduct]);

    if (!isOpen) return null;

    const requiresReasonText = reasonCode === OTHER_REASON_CODE;

    const handleSelectProduct = (product) => {
        setSelectedProduct(product);
        setSearch("");
        setSearchResults([]);
    };

    const handleChangeProduct = () => {
        setSelectedProduct(null);
    };

    const handleSave = async () => {
        if (!selectedProduct) {
            alert("Selecione um produto.");
            return;
        }

        if (!action) {
            alert("Selecione a ação desejada.");
            return;
        }

        const parsedQuantity = Number(quantity);
        if (!Number.isInteger(parsedQuantity) || parsedQuantity < 1) {
            alert("Informe uma quantidade válida (maior que 0).");
            return;
        }

        if (!reasonCode) {
            alert("Informe o motivo do ajuste.");
            return;
        }

        if (requiresReasonText && !reasonText.trim()) {
            alert("Descreva o motivo em 'Outro'.");
            return;
        }

        setLoading(true);

        try {
            await ProductService.applyManualStockAdjustment({
                searchInput: String(selectedProduct.product_id),
                action,
                quantity: parsedQuantity,
                reasonCode,
                reasonText: requiresReasonText ? reasonText.trim() : null
            });

            toast.success("Estoque atualizado com sucesso!");
            onClose();
        } catch (error) {
            console.error(error);
            toast.error(error?.response?.data?.message || "Erro ao atualizar estoque.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-60 bg-black/50 flex items-center justify-center animate-in fade-in duration-200">
            <div className="bg-white rounded-lg w-full max-w-xl p-8 shadow-lg transform transition-all">

                <h2 className="text-2xl font-semibold text-center mb-6">Atualizar Estoque</h2>

                <div className="space-y-4">
                    <div>
                        <span className="block mb-1">Produto</span>

                        {selectedProduct ? (
                            <div className="flex items-center justify-between gap-3 p-3 border border-slate-200 rounded-md bg-slate-50">
                                <div className="min-w-0">
                                    <p className="font-semibold truncate">{selectedProduct.name}</p>
                                    <p className="text-xs text-gray-500">Estoque atual: {selectedProduct.quantity}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleChangeProduct}
                                    className="text-sm text-blue-600 hover:underline shrink-0"
                                >
                                    Trocar
                                </button>
                            </div>
                        ) : (
                            <div className="relative">
                                <SearchInput
                                    value={search}
                                    onChange={setSearch}
                                    placeholder="Pesquisar produto por nome..."
                                />

                                {search.trim() && (
                                    <div className="absolute top-full left-0 w-full mt-1 max-h-56 overflow-y-auto border border-gray-200 rounded-lg shadow bg-white z-10">
                                        {searching ? (
                                            <div className="p-3 text-center text-sm text-gray-400">Buscando...</div>
                                        ) : searchResults.length > 0 ? (
                                            searchResults.map((product) => (
                                                <div
                                                    key={product.product_id}
                                                    onClick={() => handleSelectProduct(product)}
                                                    className="flex items-center justify-between px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                                                >
                                                    <span className="truncate">{product.name}</span>
                                                    <span className="text-xs text-gray-400 shrink-0 ml-2">Qtd: {product.quantity}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="p-3 text-center text-sm text-gray-400">Nenhum produto encontrado.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-between gap-4">
                        <span>Ação</span>
                        <select
                            className="w-2/3 p-2 rounded-md text-sm bg-white shadow-sm border border-slate-300 focus:ring-1 focus:ring-blue-500 outline-none"
                            value={action}
                            onChange={(e) => setAction(e.target.value)}
                        >
                            <option value="">Selecione uma opção...</option>
                            {STOCK_ADJUSTMENT_ACTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                        <span>Quantidade</span>
                        <input
                            type="number"
                            min="0"
                            step="1"
                            className="w-1/3 p-2 border-b border-gray-300 focus:border-blue-500 outline-none transition-colors"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            placeholder="0"
                        />
                    </div>

                    <div className="flex items-center justify-between gap-4">
                        <span>Motivo</span>
                        <select
                            className="w-2/3 p-2 rounded-md text-sm bg-white shadow-sm border border-slate-300 focus:ring-1 focus:ring-blue-500 outline-none"
                            value={reasonCode}
                            onChange={(e) => setReasonCode(e.target.value)}
                        >
                            <option value="">Selecione uma opção...</option>
                            {STOCK_ADJUSTMENT_REASONS.map(opt => (
                                <option key={opt.code} value={opt.code}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    {requiresReasonText && (
                        <textarea
                            className="w-full p-2 border border-slate-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                            rows={2}
                            placeholder="Descreva o motivo..."
                            value={reasonText}
                            onChange={(e) => setReasonText(e.target.value)}
                        />
                    )}
                </div>

                <div className="flex justify-end gap-4 mt-8">
                    <CustomButton
                        buttonType="save"
                        text={loading ? "Processando..." : "Salvar"}
                        onClick={handleSave}
                        disabled={loading}
                        fullWidth
                    />
                    <CustomButton
                        buttonType="cancel"
                        text="Cancelar"
                        onClick={onClose}
                        disabled={loading}
                        fullWidth
                    />
                </div>

            </div>
        </div>
    );
};

export default StockAdjustmentModal;
